'use client'

import { useEffect } from 'react'

import { useInView } from 'react-intersection-observer'

import { useSimilarIdeas } from '@/hooks/queries/ideas/useSimilarIdeas'

import { SimilarityIdeaCard } from '../similarityIdeaCard'
import { SimilarityIdeaCardSkeleton } from '../similarityIdeaCard/skeleton'

interface ISimilaritiesCardsProps {
  ideaId: string
  onClickCard: (id: string) => void
}

export const SimilaritiesCards = ({
  ideaId,
  onClickCard,
}: ISimilaritiesCardsProps) => {
  const {
    similarIdeas,
    isLoading,
    isLoadingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSimilarIdeas({
    ideaId,
    query: {
      page: 1,
      limit: 4,
    },
  })

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
  })

  useEffect(() => {
    if (inView && !isLoadingNextPage && !isLoading && hasNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, inView, isLoading, isLoadingNextPage])

  if (!ideaId) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm font-medium text-system-text-secondary">
          ID da ideia não fornecido
        </p>
      </div>
    )
  }

  if (!similarIdeas.length && !isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm font-medium text-system-text-secondary">
          Nenhuma ideia similar encontrada
        </p>
      </div>
    )
  }

  return (
    <div className="grid auto-rows-fr grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
      {isLoading
        ? [1, 2, 3, 4].map((key) => (
            <div key={key} className="flex h-full min-w-0">
              <SimilarityIdeaCardSkeleton />
            </div>
          ))
        : similarIdeas.map((idea) => (
            <div key={idea.id} className="flex h-full min-w-0">
              <SimilarityIdeaCard
                id={idea.id}
                sequence={idea.sequence}
                title={idea.title}
                description={idea.description}
                status={{
                  name: idea.ideaStatuses.name,
                  color: idea.ideaStatuses.color,
                }}
                views={16} // Mock data - pode ser adicionado na API se necessário
                likes={idea.likes}
                comments={3} // Mock data - pode ser adicionado na API se necessário
                participants={idea.participants.map((participant) => ({
                  name: participant.name,
                  image: participant.image,
                }))}
                challenge={idea.challenge}
                similarity={idea.similarity}
                onClickCard={onClickCard}
              />
            </div>
          ))}
      {isLoadingNextPage &&
        [1, 2].map((key) => (
          <div key={key} className="flex h-full min-w-0">
            <SimilarityIdeaCardSkeleton />
          </div>
        ))}
      <div ref={inViewRef} className="h-4" />
    </div>
  )
}
